import {
  ArticleTeaserBodytext,
  ArticleTeaserBodytextProps,
} from '@/components/ArticleTeaser/Bodytext/ArticleTeaserBodytext';
import { ArticleTeaserBodytextCategoryProps } from '@/components/ArticleTeaser/Bodytext/ArticleTeaserBodytext.Category';
import { ArticleTeaserBodytextDescriptionProps } from '@/components/ArticleTeaser/Bodytext/ArticleTeaserBodytext.Description';
import { ArticleTeaserBodytextFooterProps } from '@/components/ArticleTeaser/Bodytext/ArticleTeaserBodytext.Footer';
import { ArticleTeaserBodytextGroupProps } from '@/components/ArticleTeaser/Bodytext/ArticleTeaserBodytext.Group';
import { ArticleTeaserBodytextHeadlineProps } from '@/components/ArticleTeaser/Bodytext/ArticleTeaserBodytext.Headline';
import { ArticleTeaserBodytextImageProps } from '@/components/ArticleTeaser/Bodytext/ArticleTeaserBodytext.Image';
import { ArticleTeaserBodytextLineProps } from '@/components/ArticleTeaser/Bodytext/ArticleTeaserBodytext.Line';
import ErrorBoundary from '@/components/ErrorBoundary';
import { StandaloneOptionalLinkProps } from '@/components/OptionalLink';
import { LabradorComponent, StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { useAppState } from 'lib/hooks/useAppState';
import { withDebugMetadata } from 'lib/utils';
import { standaloneComponent } from 'modules/dynamic-components';
import { StandaloneImageProps } from '../../Image';

export interface StandaloneArticleTeaserBodytextProps extends StandaloneComponentProps {
  links?: {
    article?: StandaloneOptionalLinkProps;
    category?: StandaloneOptionalLinkProps;
  };
  headline?: string;
  category?: string;
  description?: string;
  footer?: string;
  image: string;
  options?: ArticleTeaserBodytextProps & {
    $headline?: ArticleTeaserBodytextHeadlineProps;
    $category?: ArticleTeaserBodytextCategoryProps;
    $description?: ArticleTeaserBodytextDescriptionProps;
    $footer?: ArticleTeaserBodytextFooterProps;
    $line?: ArticleTeaserBodytextLineProps;
    $image?: ArticleTeaserBodytextImageProps;
    $group?: ArticleTeaserBodytextGroupProps;
  };
}

const Image = standaloneComponent<StandaloneImageProps>('ContentImage');
const OptionalLink = standaloneComponent<StandaloneOptionalLinkProps>('ElementOptionalLink');

export const StandaloneArticleTeaserBodytext: StandaloneComponent<StandaloneArticleTeaserBodytextProps> = ({
  links,
  headline,
  category,
  description,
  footer,
  image,
  options,
  ...props
}) => {
  const {
    $headline: headlineProps,
    $category: categoryProps,
    $description: descriptionProps,
    $footer: footerProps,
    $line: lineProps,
    $image: imageProps,
    $group: groupProps,
    variant,
    ...baseProps
  } = options || {};

  return (
    <ErrorBoundary>
      <div>
        {headline && (
          <ArticleTeaserBodytext.Line variant={variant} {...lineProps}>
            <ArticleTeaserBodytext.Headline variant={variant} {...headlineProps}>
              {headline}
            </ArticleTeaserBodytext.Headline>
          </ArticleTeaserBodytext.Line>
        )}

        <ArticleTeaserBodytext variant={variant} {...baseProps} {...props}>
          <OptionalLink
            {...links?.article}
            content={
              <ArticleTeaserBodytext.Image {...imageProps}>
                <Image src={image} alt={category || ''} />
              </ArticleTeaserBodytext.Image>
            }
          />
          <ArticleTeaserBodytext.Group variant={variant} {...groupProps}>
            {category && (
              <OptionalLink
                {...links?.category}
                content={
                  <ArticleTeaserBodytext.Category variant={variant} {...categoryProps}>
                    {category}
                  </ArticleTeaserBodytext.Category>
                }
              />
            )}
            {description && (
              <OptionalLink
                {...links?.article}
                content={
                  <ArticleTeaserBodytext.Description variant={variant} {...descriptionProps}>
                    {description}
                  </ArticleTeaserBodytext.Description>
                }
              />
            )}
            {footer && (
              <ArticleTeaserBodytext.Footer variant={variant} {...footerProps}>
                {footer}
              </ArticleTeaserBodytext.Footer>
            )}
          </ArticleTeaserBodytext.Group>
        </ArticleTeaserBodytext>
      </div>
    </ErrorBoundary>
  );
};

export const standalone = StandaloneArticleTeaserBodytext;

const LabradorArticleTeaserBodytext: LabradorComponent = ({ type, data, meta, descendants }) => {
  const StandaloneArticleTeaserBodytext =
    standaloneComponent<StandaloneArticleTeaserBodytextProps>('ContentArticleTeaserBodytext');

  const [{ device }] = useAppState();

  const { category, categoryUrl, title, publishedUrl, kicker, footer, options } = data ?? {};

  const labradorImage = descendants?.find((descendant) => descendant.type === 'labradorImage')?.data;

  const { imageUrl } = labradorImage ?? {};

  const src = imageUrl?.[device];

  return (
    <StandaloneArticleTeaserBodytext
      links={{
        article: { href: publishedUrl },
        category: { href: categoryUrl || category },
      }}
      headline={kicker}
      category={category}
      description={title}
      footer={footer}
      image={src}
      options={options}
      data-content-index={meta.contentIndex}
      {...withDebugMetadata({ type, meta })}
    />
  );
};

export default LabradorArticleTeaserBodytext;
